import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileDown, FileSpreadsheet, RotateCcw } from 'lucide-react';
import {
  BoutonSecondaire,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampFiltre, ChampRecherche, Onglets, PanneauOnglet } from '../components/ui/Onglets';
import { ChampDate } from '../components/ui/ChampDate';
import { useToast } from '../components/ui/useToast';
import { usePerimetre } from '../site/usePerimetre';
import { useAuth } from '../auth/useAuth';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { useValeurDifferee } from '../lib/useRecherche';
import {
  exporterAbsenteisme,
  periodeIncomplete,
  useAbsenteisme,
  type AxeAbsenteisme,
  type ChoixPeriodeAbsenteisme,
  type FiltresAbsenteisme,
  type LigneAbsenteisme,
  type MesureAbsenteisme,
  type TriAbsenteisme,
} from '../lib/useAbsenteisme';

function taux(valeur: number | null): string {
  return valeur === null ? '—' : `${String(valeur).replace('.', ',')} %`;
}

function heures(valeur: number): string {
  return `${String(valeur).replace('.', ',')} h`;
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

/** « 5 absences sur 10 demi-journées », justifiées dites à part. */
function decompte(mesure: MesureAbsenteisme): string {
  const base = `${pluriel(mesure.absences, 'absence')} sur ${pluriel(mesure.demiJournees, 'demi-journée')}`;
  return mesure.justifiees > 0
    ? `${base} · dont ${mesure.justifiees} justifiée${mesure.justifiees > 1 ? 's' : ''}`
    : base;
}

const AXES: readonly { cle: AxeAbsenteisme; libelle: string }[] = [
  { cle: 'APPRENANT', libelle: 'Apprenants' },
  { cle: 'PROMOTION', libelle: 'Sessions & promotions' },
  { cle: 'MODULE', libelle: 'Modules' },
  { cle: 'FORMATION', libelle: 'Formations' },
];

const PERIODES_PAR_DEFAUT: readonly { choix: ChoixPeriodeAbsenteisme; libelle: string }[] = [
  { choix: 'DEBUT', libelle: 'Depuis le début' },
];

type Filtres = Omit<FiltresAbsenteisme, 'site'>;

const SANS_FILTRE: Filtres = {
  axe: 'APPRENANT',
  periode: 'DEBUT',
  du: '',
  au: '',
  formation: '',
  promotion: '',
  recherche: '',
  tri: 'NOM',
};

/**
 * Taux d'absentéisme (TF-461) : par apprenant, session ou promotion, bloc du
 * programme et formation, sur une période, et ses exports Excel et PDF pour les
 * financeurs.
 *
 * L'écran ne compte rien : taux, heures et repère du seuil viennent de
 * `/api/absenteisme` — feuilles d'émargement clôturées seules, absences
 * justifiées comprises, la règle des apprenants à risque (TF-427). On y vient
 * depuis « Absences & avertissements », « Émargement » et « Apprenants à
 * risque ».
 */
export function AbsenteismePage(): JSX.Element {
  const { site } = usePerimetre();
  const { peutAcceder } = useAuth();
  const toast = useToast();
  const [filtres, setFiltres] = useState<Filtres>(SANS_FILTRE);
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const [exportEnCours, setExportEnCours] = useState<'xlsx' | 'pdf' | null>(null);
  const [erreurExport, setErreurExport] = useState<string | null>(null);
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);
  const demandes: FiltresAbsenteisme = { site, ...filtres, recherche };
  const { data, isLoading, isFetching, error } = useAbsenteisme(demandes);

  useEffect(
    () => setPage(1),
    [site, taillePage, recherche, filtres.axe, filtres.periode, filtres.du, filtres.au, filtres.formation, filtres.promotion, filtres.tri],
  );

  const changer = <C extends keyof Filtres>(cle: C, valeur: Filtres[C]): void =>
    setFiltres((avant) => ({ ...avant, [cle]: valeur }));
  const choisirPeriode = (periode: ChoixPeriodeAbsenteisme): void =>
    setFiltres((avant) => ({
      ...avant,
      periode,
      // Des dates libres partent de la période affichée.
      du: periode === 'LIBRE' && !avant.du ? (data?.periode.du ?? '') : avant.du,
      au: periode === 'LIBRE' && !avant.au ? (data?.periode.au ?? '') : avant.au,
    }));

  const exporter = async (format: 'xlsx' | 'pdf'): Promise<void> => {
    setErreurExport(null);
    setExportEnCours(format);
    try {
      const { nomFichier } = await exporterAbsenteisme(format, demandes);
      toast(`${nomFichier} téléchargé`);
    } catch (refus) {
      setErreurExport(refus instanceof ErreurApi ? refus.message : 'L’export n’a pas pu être produit.');
    } finally {
      setExportEnCours(null);
    }
  };

  const filtreActif =
    filtres.recherche !== '' || filtres.formation !== '' || filtres.promotion !== '' || filtres.periode !== 'DEBUT';
  // Pendant le calcul d'un autre découpage, le précédent reste affiché, estompé,
  // avec ses propres colonnes.
  const lignes = data?.lignes ?? [];
  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  const ensemble = data?.ensemble;
  const promotions = (data?.options.promotions ?? []).filter(
    (promotion) => !filtres.formation || promotion.formation === filtres.formation,
  );
  const incomplete = periodeIncomplete(filtres);
  const exportImpossible = exportEnCours !== null || !ensemble || ensemble.apprenants === 0 || incomplete;
  const retour = peutAcceder('ped-absences')
    ? { vers: '/ped-absences', libelle: 'Absences & avertissements' }
    : { vers: '/emargement', libelle: 'Émargement' };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Taux d’absentéisme"
        sousTitre="Par apprenant, session, module et formation · exports pour les financeurs"
        actions={
          <>
            <Link
              to={retour.vers}
              className="inline-flex min-h-[44px] items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {retour.libelle}
            </Link>
            <BoutonSecondaire onClick={() => void exporter('xlsx')} disabled={exportImpossible}>
              <FileSpreadsheet className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {exportEnCours === 'xlsx' ? 'Export…' : 'Excel'}
            </BoutonSecondaire>
            <BoutonSecondaire onClick={() => void exporter('pdf')} disabled={exportImpossible}>
              <FileDown className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {exportEnCours === 'pdf' ? 'Export…' : 'PDF'}
            </BoutonSecondaire>
          </>
        }
      />

      <BarreSites />

      {erreurExport && <MessageErreur message={erreurExport} />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="Taux d’absentéisme"
          valeur={ensemble ? taux(ensemble.mesure.taux) : '—'}
          ton={ensemble?.mesure.auDelaDuSeuil ? 'red' : 'navy'}
          detail={ensemble ? decompte(ensemble.mesure) : 'Feuilles clôturées de la période'}
        />
        <Compteur
          libelle="Apprenants"
          valeur={ensemble?.apprenants ?? '—'}
          ton={ensemble && ensemble.auDelaDuSeuil > 0 ? 'warm' : 'mint'}
          detail={
            ensemble && data
              ? `dont ${ensemble.auDelaDuSeuil} au-delà du seuil de ${data.seuil} %`
              : 'Au moins une demi-journée pointée'
          }
        />
        <Compteur
          libelle="Heures suivies"
          valeur={ensemble ? heures(ensemble.mesure.heuresSuivies) : '—'}
          ton="blue"
          detail={ensemble ? `sur ${heures(ensemble.mesure.heuresPrevues)} prévues` : 'Temps réellement passé en formation'}
        />
        <Compteur
          libelle="Feuilles ouvertes"
          valeur={data?.feuillesOuvertes ?? '—'}
          ton={data && data.feuillesOuvertes > 0 ? 'warm' : 'navy'}
          detail="Pas encore clôturées : leurs demi-journées attendent"
        />
      </div>

      <Onglets
        etiquette="Découpage"
        onglets={AXES}
        actif={filtres.axe}
        onChanger={(axe) => changer('axe', axe)}
        idPanneau="panneau-absenteisme"
        actions={
          <>
            <ChampFiltre
              valeur={filtres.periode}
              onChange={(valeur) => choisirPeriode(valeur as ChoixPeriodeAbsenteisme)}
              etiquette="Période"
            >
              {(data?.options.periodes ?? PERIODES_PAR_DEFAUT).map((option) => (
                <option key={option.choix} value={option.choix}>
                  {option.libelle}
                </option>
              ))}
            </ChampFiltre>
            {filtres.periode === 'LIBRE' && (
              <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                <ChampDate libelle="Du" valeur={filtres.du} max={filtres.au || undefined} onChange={(du) => changer('du', du)} />
                <ChampDate libelle="au" valeur={filtres.au} min={filtres.du || undefined} onChange={(au) => changer('au', au)} />
              </div>
            )}
            <ChampFiltre
              valeur={filtres.formation}
              onChange={(valeur) => setFiltres((avant) => ({ ...avant, formation: valeur, promotion: '' }))}
              etiquette="Formation"
            >
              <option value="">Toutes les formations</option>
              {data?.options.formations.map((formation) => (
                <option key={formation.code} value={formation.code}>
                  {formation.titre}
                </option>
              ))}
            </ChampFiltre>
            <ChampFiltre valeur={filtres.promotion} onChange={(valeur) => changer('promotion', valeur)} etiquette="Session ou promotion">
              <option value="">Toutes les sessions et promotions</option>
              {promotions.map((promotion) => (
                <option key={promotion.cle} value={promotion.cle}>
                  {promotion.libelle} · {promotion.siteCode}
                </option>
              ))}
            </ChampFiltre>
            {filtres.axe === 'APPRENANT' && (
              <ChampRecherche
                valeur={filtres.recherche}
                onChange={(valeur) => changer('recherche', valeur)}
                etiquette="Rechercher un apprenant"
                placeholder="Nom ou prénom…"
              />
            )}
            <ChampFiltre valeur={filtres.tri} onChange={(valeur) => changer('tri', valeur as TriAbsenteisme)} etiquette="Tri">
              <option value="NOM">Ordre alphabétique</option>
              <option value="TAUX">Taux le plus haut d’abord</option>
            </ChampFiltre>
            {filtreActif && (
              <button
                type="button"
                onClick={() => setFiltres((avant) => ({ ...SANS_FILTRE, axe: avant.axe, tri: avant.tri }))}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                Réinitialiser
              </button>
            )}
          </>
        }
      />

      <PanneauOnglet id="panneau-absenteisme" actif={filtres.axe} className="space-y-6">
        {incomplete && filtres.du && filtres.au && filtres.du > filtres.au && (
          <MessageErreur message="Le premier jour de la période doit précéder le dernier." />
        )}
        {isLoading && <Chargement libelle="Calcul des taux…" />}
        {error && (
          <MessageErreur message={error instanceof ErreurApi ? error.message : 'Les taux n’ont pas pu être chargés.'} />
        )}
        {data && !isLoading && !error && lignes.length === 0 && (
          <p className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">
            {filtreActif
              ? 'Aucune demi-journée pointée sur une feuille clôturée ne correspond aux filtres.'
              : 'Aucune demi-journée pointée sur une feuille clôturée dans ce périmètre.'}
          </p>
        )}

        {data && lignes.length > 0 && (
          <div className={`overflow-hidden rounded-2xl bg-white shadow-card transition-opacity ${isFetching ? 'opacity-70' : ''}`}>
            {/* Sous l'ordinateur, une carte par ligne : le tableau y défilerait de côté. */}
            <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 xl:hidden">
              {affichees.map((ligne) => (
                <li key={ligne.cle} className="space-y-3 border-gray-100 p-4 md:border-b md:odd:border-r">
                  <Identite ligne={ligne} axe={data.axe} />
                  <div className="grid grid-cols-2 gap-3">
                    <Taux mesure={ligne.mesure} seuil={data.seuil} />
                    <Heures mesure={ligne.mesure} libelle />
                  </div>
                </li>
              ))}
            </ul>
            <div className="relative hidden overflow-x-auto xl:block">
              <table className="w-full min-w-[960px] text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    {[ENTETES[data.axe], 'Taux d’absentéisme', 'Demi-journées', 'Heures suivies'].map((colonne) => (
                      <th key={colonne} className={CLASSE_TH}>
                        {colonne}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {affichees.map((ligne) => (
                    <tr key={ligne.cle} className="transition hover:bg-gray-50">
                      <td className={`${CLASSE_TD} max-w-[360px]`}>
                        <Identite ligne={ligne} axe={data.axe} />
                      </td>
                      <td className={CLASSE_TD}>
                        <Taux mesure={ligne.mesure} seuil={data.seuil} />
                      </td>
                      <td className={CLASSE_TD}>
                        <DemiJournees mesure={ligne.mesure} />
                      </td>
                      <td className={CLASSE_TD}>
                        <Heures mesure={ligne.mesure} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pages={Math.max(1, Math.ceil(lignes.length / taillePage))}
              total={lignes.length}
              affiches={affichees.length}
              taillePage={taillePage}
              onPage={setPage}
              onChangerTaille={setTaillePage}
              uniteLabel={UNITES[data.axe]}
            />
          </div>
        )}

      </PanneauOnglet>

      {data && (
        <p className="text-[11px] text-codialis-gray">
          Période : {data.periode.libelle} · demi-journées d’absence sur demi-journées pointées, feuilles d’émargement
          clôturées seules · absences justifiées comprises · un retard compte comme une présence · en rouge au-delà du
          seuil de l’organisme ({data.seuil} %) · calculé le{' '}
          {new Date(data.calculeLe).toLocaleString('fr-FR', {
            timeZone: 'Indian/Reunion',
            dateStyle: 'short',
            timeStyle: 'short',
          })}
          . L’export Excel reprend les quatre découpages ; le PDF, celui qui est affiché.
        </p>
      )}
    </div>
  );
}

const ENTETES: Record<AxeAbsenteisme, string> = {
  APPRENANT: 'Apprenant',
  PROMOTION: 'Session ou promotion',
  MODULE: 'Bloc du programme',
  FORMATION: 'Formation',
};

const UNITES: Record<AxeAbsenteisme, string> = {
  APPRENANT: 'apprenant',
  PROMOTION: 'promotion',
  MODULE: 'bloc',
  FORMATION: 'formation',
};

const CLASSE_TH = 'px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray xl:px-4';
const CLASSE_TD = 'px-3 py-3 align-top xl:px-4';

function Detail({ children }: { children: ReactNode }): JSX.Element {
  return <div className="text-[11px] text-codialis-gray">{children}</div>;
}

function Identite({ ligne, axe }: { ligne: LigneAbsenteisme; axe: AxeAbsenteisme }): JSX.Element {
  const formation = ligne.formation?.titre ?? 'Sans formation rattachée';
  return (
    <div className="min-w-0">
      {ligne.apprenantId ? (
        <Link
          to={`/adm-dossier/${ligne.apprenantId}`}
          className="block truncate font-semibold text-codialis-navy hover:text-codialis-sky hover:underline [@media(pointer:coarse)]:leading-10"
        >
          {ligne.libelle}
        </Link>
      ) : (
        <div
          title={ligne.libelle}
          className={`line-clamp-2 break-words font-semibold ${
            ligne.bloc === null && axe === 'MODULE' ? 'text-codialis-gray' : 'text-codialis-navy'
          }`}
        >
          {ligne.libelle}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1 pt-0.5">
        {ligne.sites.map((code) => (
          <PastilleSite key={code} site={code} />
        ))}
        {ligne.mesure.auDelaDuSeuil && <Pastille texte="Au-delà du seuil" ton="danger" />}
      </div>
      {axe === 'APPRENANT' && (
        <Detail>
          {formation}
          {ligne.promotion && ligne.promotion.libelle !== formation ? ` · ${ligne.promotion.libelle}` : ''}
        </Detail>
      )}
      {(axe === 'PROMOTION' || axe === 'MODULE') && <Detail>{formation}</Detail>}
      {axe !== 'APPRENANT' && <Detail>{pluriel(ligne.apprenants, 'apprenant')}</Detail>}
    </div>
  );
}

function Taux({ mesure, seuil }: { mesure: MesureAbsenteisme; seuil: number }): JSX.Element {
  return (
    <div className="min-w-0 text-xs">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-codialis-gray xl:hidden">Absentéisme</div>
      <div
        className={`font-display text-lg font-bold ${mesure.auDelaDuSeuil ? 'text-red-600' : 'text-codialis-navy'}`}
        title={`Seuil de l’organisme : ${seuil} %`}
      >
        {taux(mesure.taux)}
      </div>
      <Detail>{decompte(mesure)}</Detail>
    </div>
  );
}

function DemiJournees({ mesure }: { mesure: MesureAbsenteisme }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5 text-xs text-codialis-navy">
      <div>{pluriel(mesure.demiJournees, 'pointée')}</div>
      <Detail>
        {pluriel(mesure.presences, 'présence')}
        {mesure.retards > 0 ? ` dont ${pluriel(mesure.retards, 'retard')}` : ''}
      </Detail>
      <Detail>{pluriel(mesure.nonJustifiees, 'absence non justifiée', 'absences non justifiées')}</Detail>
    </div>
  );
}

function Heures({ mesure, libelle = false }: { mesure: MesureAbsenteisme; libelle?: boolean }): JSX.Element {
  return (
    <div className="min-w-0 text-xs">
      {libelle && (
        <div className="text-[11px] font-semibold uppercase tracking-wider text-codialis-gray">Heures suivies</div>
      )}
      <div className="font-semibold text-codialis-navy">{heures(mesure.heuresSuivies)}</div>
      <Detail>sur {heures(mesure.heuresPrevues)} prévues</Detail>
    </div>
  );
}
